import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Tooltip } from 'react-tooltip';
import { BusinessIdea } from '../../../services/businessIdeaTypes';
import { getSavedBusinessIdeas } from '../../../services/savedIdeasService';

const IdeasTab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ideas, setIdeas] = useState<BusinessIdea[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const templateId = useMemo(() => {
    return (location as any)?.state?.templateId as string | undefined;
  }, [location]);

  useEffect(() => {
    loadIdeas();
  }, []);

  useEffect(() => {
    const handler = () => {
      void loadIdeas();
    };

    window.addEventListener('bp-data-changed', handler);
    return () => window.removeEventListener('bp-data-changed', handler);
  }, []);

  const loadIdeas = async () => {
    try {
      setIsLoading(true);
      const data = await getSavedBusinessIdeas();
      setIdeas(data);
    } catch (error) {
      console.error('Error loading ideas:', error);
      toast.error('Failed to load business ideas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePlan = (idea: BusinessIdea) => {
    navigate('/business-plan/new', { state: { selectedIdea: idea, templateId } });
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse rounded-lg border border-gray-200 p-6">
            <div className="h-5 w-3/4 bg-gray-200 rounded mb-3"></div>
            <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-2/3 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (ideas.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">💡</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No business ideas yet</h3>
        <p className="text-gray-600 mb-6">Generate business ideas first, then create plans from them</p>
        <button
          onClick={() => navigate('/idea-generator')}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span>Generate Ideas</span>
        </button>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {ideas.map(idea => (
        <div
          key={idea.id}
          className="group relative rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md"
        >
          {/* Idea Title */}
          <h3 className="mb-2 font-semibold text-gray-900 line-clamp-2">
            {idea.title}
          </h3>

          {/* Description */}
          <p className="mb-4 text-sm text-gray-600 line-clamp-3">
            {idea.description}
          </p>

          {/* Tags */}
          {idea.industry && (
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                {idea.industry}
              </span>
              {idea.targetMarket && (
                <span className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                  {idea.targetMarket}
                </span>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => handleCreatePlan(idea)}
              className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
              data-tooltip-id={`bp-idea-create-${idea.id}`}
              data-tooltip-content="Create a plan from this idea"
            >
              Create Plan
            </button>
            <Tooltip id={`bp-idea-create-${idea.id}`} place="top" />
            <button
              onClick={() => navigate('/idea-generator', { state: { ideaId: idea.id } })}
              className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              aria-label="View idea details"
              data-tooltip-id={`bp-idea-view-${idea.id}`}
              data-tooltip-content="View this idea"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
            <Tooltip id={`bp-idea-view-${idea.id}`} place="top" />
          </div>
        </div>
      ))}
    </div>
  );
};

export default IdeasTab;
